/**
 * Tipos de la categoría **Gestión** (`/api/gestion/*`).
 *
 * La categoría agrupa doce pantallas del panel anterior; esta primera entrega es
 * **Inventario** (listado de stock, entradas y salidas), portado de
 * `Inventario.php` + `Inventario_model` (`producto`, `entrada`/`detalle_entrada`
 * y `salida`/`detalle_salida`).
 */

/** Producto del listado de inventario. */
export interface ProductoInventario {
  id: number;
  nombre: string;
  tipo: string;
  activo: boolean;
  stock: number;
  unidad: string;
  precio_venta: number | null;
  precio_compra: number | null;
  color: string;
  categoria: string;
  subcategoria: string;
  categoria_id: number | null;
  codigo_plu: string;
  en_market: boolean;
  capacidad_maxima: number | null;
  capacidad_minima: number | null;
  agotado: boolean;
  bajo_minimo: boolean;
  porcentaje_tanque: number | null;
}

/** Contadores de la cabecera del listado. */
export interface ResumenInventario {
  total: number;
  agotados: number;
  bajo_minimo: number;
  inactivos: number;
  valor_compra: number;
}

/** Respuesta del listado de inventario. */
export interface InventarioRespuesta {
  productos: ProductoInventario[];
  resumen: ResumenInventario;
  tipos: string[];
}

/** Movimiento de inventario (entrada o salida). */
export interface MovimientoInventario {
  detalle_id: number;
  movimiento_id: number;
  producto_id: number;
  producto: string;
  tipo_producto: string;
  unidad: string;
  cantidad: number;
  descripcion: string;
  fecha: string | null;
  created_at: string | null;
  usuario: string;
  /** Sólo en entradas: comprobante de la compra que generó el ingreso. */
  factura: string | null;
  proveedor: string | null;
  compra_id: number | null;
  tipo: 'entrada' | 'salida';
}

/** Respuesta de los listados de movimientos. */
export interface MovimientosInventarioRespuesta {
  movimientos: MovimientoInventario[];
  total: number;
  cantidad: number;
}

/** Compra del producto que todavía no se ha almacenado. */
export interface CompraPendiente {
  compra_id: number;
  detalle_id: number;
  documento: string;
  proveedor: string;
  fecha: string | null;
  cantidad: number;
}

/** Filtros de los listados de movimientos. */
export interface FiltrosInventario {
  desde?: string;
  hasta?: string;
  producto_id?: number;
  search?: string;
}

/** Datos para registrar una entrada. */
export interface EntradaInventario {
  producto_id: number;
  cantidad: number;
  razon: string;
  fecha_entrada: string;
  purchase_id?: number;
}

/** Datos para registrar una salida. */
export interface SalidaInventario {
  producto_id: number;
  cantidad: number;
  razon: string;
  fecha_salida: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Clientes                                  */
/* -------------------------------------------------------------------------- */

/** Tipos de documento que guarda `cliente.tipo_doc` (`1` DNI, `6` RUC). */
export const TIPOS_DOCUMENTO = [
  { valor: '1', etiqueta: 'DNI', longitud: 8 },
  { valor: '6', etiqueta: 'RUC', longitud: 11 },
] as const;

/** Placa activa (`placa.estado_placa = 1`) de un cliente. */
export interface PlacaCliente {
  id: number;
  numero_placa: string;
  descripcion_placa: string;
  tipo_placa_id: number | null;
  tipo_placa: string | null;
}

/** Cliente del listado o de la ficha. */
export interface Cliente {
  id: number;
  tipo: string | null;
  tipo_doc: string | null;
  numero_doc: string | null;
  nombre: string | null;
  nombre_comercial: string | null;
  direccion: string | null;
  telefono: string | null;
  correo: string | null;
  estado: boolean;
  estado_sunat: string | null;
  condicion_sunat: string | null;
  withholding: boolean;
  withholding_percentage: number;
  portal_activo: boolean;
  portal_fecha_registro: string | null;
  portal_ultimo_acceso: string | null;
  created_at: string | null;
  placas: PlacaCliente[];
  cantidad_placas: number;
}

/** Contadores globales de la cabecera del listado. */
export interface ResumenClientes {
  total: number;
  activos: number;
  inhabilitados: number;
  con_portal: number;
  con_placas: number;
  con_retencion: number;
}

/** Respuesta del listado de clientes. */
export interface ClientesRespuesta {
  clientes: Cliente[];
  /** Clientes no eliminados (sin filtros). */
  total: number;
  /** Clientes que cumplen los filtros. */
  filtrados: number;
  limit: number;
  offset: number;
  resumen: ResumenClientes;
  tipos: string[];
}

/** Motivo por el que un cliente no se puede dar de baja. */
export interface BloqueoCliente {
  clave: string;
  etiqueta: string;
  total: number;
}

/** Comprobantes, vales y créditos del cliente. */
export interface BloqueosCliente {
  detalle: BloqueoCliente[];
  total: number;
  eliminar: boolean;
}

/** Ficha completa de un cliente. */
export interface ClienteFichaRespuesta {
  cliente: Cliente;
  bloqueos: BloqueosCliente;
}

/** Filtros del listado. */
export interface FiltrosClientes {
  search?: string;
  /** `1` habilitados (por defecto), `0` inhabilitados, `todos`. */
  estado?: string;
  /** `1` con portal, `0` sin portal. */
  portal?: string;
  /** `con` o `sin` placas activas. */
  placas?: string;
  orden?: string;
  dir?: string;
  limit?: number;
  offset?: number;
}

/** Datos del alta de cliente (`/cliente/post`). */
export interface NuevoCliente {
  tipo_doc: string;
  numero_doc: string;
  nombre: string;
  tipo?: string;
  nombre_comercial?: string;
  direccion?: string;
  telefono?: string;
  correo?: string;
  withholding?: boolean;
  withholding_percentage?: number;
}

/** Datos de la edición de la ficha (`/cliente/update`). */
export interface EdicionCliente {
  tipo?: string;
  nombre?: string;
  nombre_comercial?: string;
  direccion?: string;
  telefono?: string;
  correo?: string;
  estado?: boolean;
  estado_sunat?: string;
  condicion_sunat?: string;
  withholding?: boolean;
  withholding_percentage?: number;
}

/** Acciones del portal del cliente. */
export type AccionPortal = 'activar' | 'desactivar' | 'regenerar';

/** Respuesta de una acción del portal (la contraseña sólo se devuelve una vez). */
export interface AccionPortalRespuesta {
  accion: AccionPortal;
  portal_activo: boolean;
  usuario?: string;
  password?: string;
  mensaje: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Productos                                 */
/* -------------------------------------------------------------------------- */

/** Tipos de producto del catálogo. */
export const TIPOS_PRODUCTO = ['COMBUSTIBLE', 'LUBRICANTE', 'COMBO', 'EXTRA'] as const;

export type TipoProducto = (typeof TIPOS_PRODUCTO)[number];

/** Producto del catálogo, con el stock real (el del tanque si es combustible). */
export interface ProductoGestion {
  id: number;
  nombre: string;
  nombre_abreviado: string | null;
  tipo: string;
  unidad: string | null;
  foto_producto: string | null;
  color: string | null;
  categoria: string | null;
  subcategoria: string | null;
  precio_venta: number | null;
  precio_compra: number | null;
  estado: boolean;
  mostrar_en_market: boolean;
  /** Stock «real»: la última medición del tanque en los combustibles. */
  stock: number;
  /** `producto.stock` tal cual (en combustibles suele no coincidir con el tanque). */
  stock_producto: number;
  /** Sólo en combos: unidades que se pueden armar con los componentes. */
  stock_disponible: number | null;
  capacidad_maxima_tanque: number | null;
  capacidad_minima_tanque: number | null;
  porcentaje_tanque: number | null;
  bajo_minimo: boolean;
  tanque_id: number | null;
  ric_physical: number | null;
  ric_stamptime: string | null;
  ric_date: string | null;
  ric_logico: number | null;
  ric_diferencia: number | null;
  sin_medicion: boolean;
  dias_sin_medicion: number | null;
  actualizado_at: string | null;
  codigo_plu: string | null;
  codigo_item_api: string | null;
  external_code_edsuite: string | null;
  codigo_contable: string | null;
  barcode: string | null;
}

/** Contadores de la cabecera de Productos. */
export interface ResumenProductos {
  total: number;
  activos: number;
  inactivos: number;
  combustibles: number;
  lubricantes: number;
  combos: number;
  extras: number;
  en_market: number;
  /** Tanques con medición entre los combustibles devueltos. */
  tanques: number;
  tanques_sin_medicion: number;
  tanques_bajo_minimo: number;
  porcentaje_promedio: number | null;
}

/** Respuesta del listado de productos. */
export interface ProductosRespuesta {
  productos: ProductoGestion[];
  total: number;
  resumen: ResumenProductos;
  tipos: string[];
}

/** Medición RIC del tanque de un producto. */
export interface MedicionProducto {
  id: number;
  fecha: string | null;
  stamptime: string | null;
  anterior: number;
  ingresos: number;
  salidas: number;
  logico: number;
  fisico: number;
  diferencia: number;
  varilla: number;
  agua: number;
  comentario: string | null;
  vigente: boolean;
  ejecutor: string | null;
  porcentaje: number | null;
  dias: number | null;
}

/** Respuesta de las mediciones de un producto. */
export interface MedicionesRespuesta {
  producto: {
    id: number;
    nombre: string;
    tipo: string;
    color: string | null;
    unidad: string | null;
    tanque_id: number;
    capacidad_maxima_tanque: number | null;
    capacidad_minima_tanque: number | null;
  };
  mediciones: MedicionProducto[];
  total: number;
}

/** Filtros del listado de productos. */
export interface FiltrosProductos {
  search?: string;
  tipo?: string;
  /** `1` activos (por defecto), `0` inactivos, `todos`. */
  estado?: string;
  /** `1` visibles en el market, `0` ocultos. */
  market?: string;
  orden?: string;
  dir?: string;
}

/** Subcategoría de producto (`producto_subcategoria`). */
export interface SubcategoriaProducto {
  id: number;
  categoria_id: number;
  nombre: string;
  descripcion: string | null;
  estado: number;
}

/** Categoría de producto (`producto_categoria`). */
export interface CategoriaProducto {
  id: number;
  nombre: string;
  descripcion: string | null;
  estado: number;
  /** Productos que la usan (el original no lo mostraba). */
  productos: number;
  subcategorias: SubcategoriaProducto[];
}

/** Precio programado (`producto_precio_programado`). */
export interface PrecioProgramado {
  id: number;
  producto_id: number;
  producto: string | null;
  tipo: string | null;
  precio_venta_anterior: number;
  precio_venta_nuevo: number;
  precio_compra_nuevo: number | null;
  precio_venta_actual: number | null;
  fecha_programada: string | null;
  fecha_ejecucion: string | null;
  estado: string | null;
  motivo: string | null;
  usuario: string | null;
  created_at: string | null;
}

/** Combo del producto: sus componentes (`combo_items`). */
export interface ComboItem {
  producto_id: number;
  cantidad: number;
}

/** Ficha de un producto para el alta y la edición (`Producto::edit`). */
export interface ProductoFicha {
  id: number;
  nombre: string;
  nombre_abreviado: string | null;
  tipo: string | null;
  precio_venta: number;
  precio_compra: number;
  stock: number;
  estado: number | null;
  estado_venta: number | null;
  mostrar_en_market: number | null;
  categoria_id: number | null;
  subcategoria_id: number | null;
  categoria: string | null;
  subcategoria: string | null;
  unidad_medida_id: number | null;
  unidad_medida: string | null;
  color: string | null;
  capacidad_maxima_tanque: number | null;
  capacidad_minima_tanque: number | null;
  bonus_code: string | null;
  bonus_description: string | null;
  barcode: string | null;
  codigo_contable: string | null;
  external_code_edsuite: string | null;
  codigo_plu: string | null;
  orden: number | null;
  foto_producto: string | null;
  combo_items: ComboItem[];
}

/** Datos editables de un producto (alta y edición). */
export interface DatosProducto {
  nombre: string;
  tipo: string;
  precio_venta: number;
  precio_compra: number;
  estado: number;
  mostrar_en_market: boolean;
  external_code_edsuite?: string | null;
  codigo_contable?: string | null;
  categoria_id?: number | null;
  subcategoria_id?: number | null;
  nombre_abreviado?: string | null;
  color?: string | null;
  capacidad_maxima_tanque?: number | null;
  capacidad_minima_tanque?: number | null;
  bonus_code?: string | null;
  bonus_description?: string | null;
  barcode?: string | null;
  combo_items?: ComboItem[];
}

/** Respuesta de la ficha de un producto. */
export interface ProductoFichaRespuesta {
  producto: ProductoFicha;
  programados: {
    id: number;
    precio_venta_anterior: number;
    precio_venta_nuevo: number;
    fecha_programada: string | null;
    motivo: string | null;
  }[];
}

/* ------------------------------------------------------------------ *
 * Diferencia diaria de combustibles
 * ------------------------------------------------------------------ */

/** Producto con lo que marcó el contómetro en un día. */
export interface ContometroProducto {
  id: number;
  nombre: string;
  volumen: number;
  importe: number;
}

/** Turno resumido de un día de la diferencia diaria. */
export interface TurnoDiferencia {
  id: number;
  grifero: string | null;
  hora_llegada: string | null;
  hora_salida: string | null;
}

/** Un día de la diferencia diaria. */
export interface DiaDiferencia {
  dia: string;
  turnos: TurnoDiferencia[];
  contometro: ContometroProducto[];
  visa: number;
  izipay: number;
  transferencia: number;
  boveda: number;
  entregado: number;
  gastos: number;
  ingresos: number;
  combustible: number;
  venta_total: number;
  credito: number;
  descuento_contado: number;
  descuento_credito: number;
  pendiente: number;
}

/** Totales del mes de la diferencia diaria. */
export interface TotalesDiferencia {
  combustible: number;
  venta_total: number;
  visa: number;
  izipay: number;
  transferencia: number;
  boveda: number;
  entregado: number;
  gastos: number;
  ingresos: number;
  credito: number;
  descuento_contado: number;
  descuento_credito: number;
  pendiente: number;
}

/** Respuesta de la diferencia diaria. */
export interface DiferenciaDiariaRespuesta {
  mes: string;
  fecha_inicio: string;
  fecha_fin: string;
  productos: string[];
  dias: DiaDiferencia[];
  totales: TotalesDiferencia;
}

/* ------------------------------------------------------------------ *
 * Placas, tipos de placa y descuentos del cliente
 * ------------------------------------------------------------------ */

/** Descuento activo de una placa por producto (`descuento_cliente`). */
export interface DescuentoPlaca {
  id: number;
  cliente_id: number;
  placa_id: number;
  producto_id: number;
  descuento: number | string;
  estado: number;
  producto_nombre: string | null;
}

/** Placa del cliente con sus descuentos (`placa` + `descuento_cliente`). */
export interface PlacaConDescuentos {
  id: number;
  numero_placa: string;
  descripcion_placa: string | null;
  cliente_id: number;
  cliente_tipo_placa_id: number | null;
  cliente_tipo_placa: string | null;
  estado_placa: number;
  descuentos: DescuentoPlaca[];
}

/** Tipo de placa (`cliente_tipo_placa`). */
export interface TipoPlaca {
  id: number;
  descripcion: string;
  estado: number | null;
  /** Placas activas de ese tipo (lo añade el listado). */
  placas?: number;
}

/** Respuesta de las placas de un cliente. */
export interface PlacasClienteRespuesta {
  cliente: { id: number; nombre: string | null; numero_doc: string | null; tipo_doc: string | null };
  placas: PlacaConDescuentos[];
  tipos_placa: TipoPlaca[];
}

/** Producto con el descuento que tiene puesto una placa. */
export interface ProductoConDescuento {
  id: number;
  nombre: string;
  nombre_item_api: string | null;
  tipo: string | null;
  precio_venta: number | string;
  descuento_id: number | null;
  descuento: number | null;
  activo: boolean;
}

/** Respuesta de los descuentos de una placa. */
export interface DescuentosPlacaRespuesta {
  placa: {
    id: number;
    numero_placa: string;
    descripcion_placa: string | null;
    cliente_id: number;
    cliente_nombre: string | null;
    cliente_doc: string | null;
    cliente_tipo_placa: string | null;
  };
  productos: ProductoConDescuento[];
}

/** Documento devuelto por la consulta a SUNAT/RENIEC del alta de clientes. */
export interface DocumentoSunat {
  numero: string;
  nombre: string | null;
  direccion: string | null;
  estado: string | null;
  condicion: string | null;
}
